import { useFeatureFlags } from '@brightdrop/feature-flags-client';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  AlertRecord,
  GmResponseCommonGmPageAlertRecord,
} from '~/api/generated-api';
import { EMER_FLAG } from '~/common/models/featureFlags.model';
import { isEmerAlertRecord } from '~/utils/alerts/emerAlerts.utils';

interface UseAlertsPaginationProps {
  data: GmResponseCommonGmPageAlertRecord | undefined;
}

export const useAlertsPagination = ({ data }: UseAlertsPaginationProps) => {
  const [alerts, setAlerts] = useState<AlertRecord[]>([]);
  const { getFlag } = useFeatureFlags();
  const isEmerEnabled = Boolean(getFlag(EMER_FLAG));

  /* Compared against every received alert, including hidden ones, so paging matches the API total */
  const hasMoreAlerts = useMemo(() => {
    return alerts.length < (data?.result?.total_items || 0);
  }, [data, alerts]);

  const visibleAlerts = useMemo(
    () =>
      isEmerEnabled
        ? alerts
        : alerts.filter((alert) => !isEmerAlertRecord(alert)),
    [alerts, isEmerEnabled]
  );

  /* Every received alert is hidden, so nothing renders that could trigger the next page on scroll */
  const needsNextPage =
    hasMoreAlerts && alerts.length > 0 && visibleAlerts.length === 0;

  /* Removes an alert from the list in order to avoid calling the API on every unpin and dismiss */
  const handleRemoveNotification = useCallback((alert: AlertRecord) => {
    /* Remove the notification from the list */
    setAlerts((prevAlerts) =>
      prevAlerts.filter((a) => {
        if (alert.type === 'GROUP') {
          return a?.lastGroupEvent?.id !== alert.lastGroupEvent?.id;
        }
        return a.id !== alert.id;
      })
    );
  }, []);

  /* Adds a new alert to the list in order to avoid calling the API on every pin/unpin */
  const handleAddNotification = useCallback((alert: AlertRecord) => {
    setAlerts((prevAlerts) => {
      const newAlerts = [...prevAlerts, alert];
      newAlerts.sort((a, b) => {
        const aDate = new Date(a.lastEventDateTime);
        const bDate = new Date(b.lastEventDateTime);
        return bDate.getTime() - aDate.getTime();
      });
      return newAlerts;
    });
  }, []);

  useEffect(() => {
    if (data?.result?.items) {
      /* The conditionals in this updater are necessary to avoid duplicate alerts being populated
       * on localhost.  This is due to react strict mode being enabled locally, which causes
       * useEffects to be called twice.
       */
      setAlerts((existingAlerts) => {
        /* If there are no existing alerts, set the alerts to the new alerts. */
        if (!existingAlerts.length) {
          return data?.result?.items || [];
        }

        const newAlerts = data?.result?.items?.filter(
          (a) => !existingAlerts.some((e) => e.id === a.id)
        );
        return [...existingAlerts, ...(newAlerts || [])];
      });
    }
  }, [data?.result?.items]);

  return {
    alerts: visibleAlerts,
    hasMoreAlerts,
    needsNextPage,
    handleRemoveNotification,
    handleAddNotification,
  };
};
